// src/services/ImageOptimizationService.js
// Reduces image size before upload to minimize Firebase Storage costs

class ImageOptimizationService {
  /**
   * Compress and resize image before uploading
   * @param {File} file - Original image file
   * @param {Object} options - Compression options
   * @returns {Promise<Blob>} Optimized image blob
   */
  static async optimizeImage(file, options = {}) {
    const {
      maxWidth = 1200,
      maxHeight = 1200,
      quality = 0.8,
      format = 'image/jpeg'
    } = options;

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          try {
            // Calculate new dimensions while maintaining aspect ratio
            let width = img.width;
            let height = img.height;
            
            if (width > maxWidth || height > maxHeight) {
              const aspectRatio = width / height;
              
              if (width > height) {
                width = maxWidth;
                height = maxWidth / aspectRatio;
              } else {
                height = maxHeight;
                width = maxHeight * aspectRatio;
              }
            }
            
            // Create canvas for resizing
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            
            const ctx = canvas.getContext('2d');
            
            // Enable image smoothing for better quality
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            
            // Draw resized image
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to blob with compression
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  console.log(`✅ Image optimized: ${(file.size / 1024).toFixed(2)}KB → ${(blob.size / 1024).toFixed(2)}KB`);
                  resolve(blob);
                } else {
                  reject(new Error('Failed to create blob'));
                }
              },
              format,
              quality
            );
          } catch (error) {
            reject(error);
          }
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Create thumbnail version of image
   * @param {File} file - Original image file
   * @returns {Promise<Blob>} Thumbnail blob
   */
  static async createThumbnail(file) {
    return this.optimizeImage(file, {
      maxWidth: 400,
      maxHeight: 400,
      quality: 0.7,
      format: 'image/jpeg'
    });
  }

  /**
   * Optimize multiple images in batch
   * @param {File[]} files - Array of image files
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Blob[]>} Array of optimized blobs
   */
  static async optimizeMultiple(files, onProgress = null) {
    const optimizedImages = [];
    
    for (let i = 0; i < files.length; i++) {
      try {
        const optimized = await this.optimizeImage(files[i]);
        optimizedImages.push(optimized);
        
        if (onProgress) {
          onProgress({
            current: i + 1,
            total: files.length,
            percentage: Math.round(((i + 1) / files.length) * 100)
          });
        }
      } catch (error) {
        console.error(`❌ Failed to optimize image ${i + 1}:`, error);
        // Fallback to original file if optimization fails
        optimizedImages.push(files[i]);
      }
    }
    
    return optimizedImages;
  }

  /**
   * Validate image file
   * @param {File} file - Image file to validate
   * @returns {Object} Validation result
   */
  static validateImage(file) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Invalid file type. Please upload JPEG, PNG, or WebP images.'
      };
    }
    
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'File size too large. Maximum size is 10MB.'
      };
    }
    
    return { valid: true };
  }

  /**
   * Get estimated storage cost savings
   * @param {File[]} originalFiles - Original files
   * @param {Blob[]} optimizedBlobs - Optimized blobs
   * @returns {Object} Cost savings info
   */
  static calculateSavings(originalFiles, optimizedBlobs) {
    const originalSize = originalFiles.reduce((sum, file) => sum + file.size, 0);
    const optimizedSize = optimizedBlobs.reduce((sum, blob) => sum + blob.size, 0);
    const savedBytes = originalSize - optimizedSize;
    const savingsPercent = ((savedBytes / originalSize) * 100).toFixed(2);
    
    return {
      originalSize: (originalSize / 1024 / 1024).toFixed(2) + ' MB',
      optimizedSize: (optimizedSize / 1024 / 1024).toFixed(2) + ' MB',
      savedSize: (savedBytes / 1024 / 1024).toFixed(2) + ' MB',
      savingsPercent: savingsPercent + '%'
    };
  }

  /**
   * Convert blob to File object for uploading
   * @param {Blob} blob - Image blob
   * @param {string} fileName - Original filename
   * @returns {File} File object
   */
  static blobToFile(blob, fileName) {
    const timestamp = Date.now();
    const name = `optimized_${timestamp}_${fileName}`;
    return new File([blob], name, { type: blob.type });
  }

  /**
   * Generate responsive image sizes
   * @param {File} file - Original image
   * @returns {Promise<Object>} Object with different sizes
   */
  static async generateResponsiveSizes(file) {
    const [thumbnail, medium, large] = await Promise.all([
      this.optimizeImage(file, { maxWidth: 400, maxHeight: 400, quality: 0.7 }),
      this.optimizeImage(file, { maxWidth: 800, maxHeight: 800, quality: 0.8 }),
      this.optimizeImage(file, { maxWidth: 1200, maxHeight: 1200, quality: 0.85 })
    ]);
    
    return {
      thumbnail,
      medium,
      large
    };
  }
}

export default ImageOptimizationService;